// /verify-email?token=... — the URL the verification email points at.

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';

export function VerifyEmail(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<
    | { kind: 'verifying' }
    | { kind: 'success' }
    | { kind: 'error'; message: string }
  >({ kind: 'verifying' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: t('verifyEmail.error.missingToken') });
      return;
    }
    void (async () => {
      try {
        await authApi.verifyEmail(token);
        setState({ kind: 'success' });
      } catch (err) {
        const envelope = extractApiError(err);
        setState({
          kind: 'error',
          message: envelope?.message ?? t('verifyEmail.error.invalidOrExpired'),
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="card">
      <h2>{t('verifyEmail.title')}</h2>
      {state.kind === 'verifying' && <p>{t('verifyEmail.verifying.body')}</p>}
      {state.kind === 'success' && (
        <>
          <div className="banner banner--success">
            <strong>{t('verifyEmail.success.banner')}</strong>
          </div>
          <p>
            <Link to="/login">{t('verifyEmail.success.cta', { signIn: t('common:nav.signIn') })}</Link>
          </p>
        </>
      )}
      {state.kind === 'error' && (
        <>
          <div className="banner banner--error">
            <strong>{state.message}</strong>
          </div>
          <p>
            <Trans
              i18nKey="verifyEmail.error.newLinkPrompt"
              t={t}
              values={{ signIn: t('common:nav.signIn') }}
              components={[<Link key="signin" to="/login" />]}
            />
          </p>
        </>
      )}
    </div>
  );
}
